import { Box, Tooltip } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { arrowSxDefault, tooltipSxDefault } from "./Styles";
import {PLACEMENT_MODEL} from "../Helpers/PositionModel";

/**
 * @author Jhovanny Fragoso Marquez
 * @jobTitle Frontend Developer
 * @email jfragoso@ibl.mx
 *
 * @component CustomTooltip
 * @description
 * A reusable tooltip component that displays a tooltip when the text is truncated
 * or always wraps its children depending on the provided props.
 *
 * @param {Object} props - Component props.
 * @param {string} props.text - Text to display inside the tooltip.
 * @param {React.ReactNode} [props.children=null] - Optional child element to wrap with the tooltip.
 *                                                  If not provided, a `<span>` with the text is rendered.
 * @param {("bottom"|"top"|"left"|"right")} [props.placement="bottom"] - Tooltip position relative to the element.
 * @param {boolean} [props.arrow=false] - Whether to display an arrow on the tooltip.
 * @param {Object} [props.tooltipSx=tooltipSxDefault] - Custom `sx` styles for the tooltip container.
 * @param {Object} [props.arrowSx=arrowSxDefault] - Custom `sx` styles for the tooltip arrow.
 * @param {string} [props.classes=""] - Optional CSS classes applied to the fallback `<span>` element.
 * @param {string} [props.tooltipClasses=""] - Optional CSS classes applied to the fallback `<Tooltip>` element.
 * @param {boolean} [props.showIfTruncated=false] - Whether to only show the tooltip if the text is visually truncated.
 * @param {JSX.Element} [props.icon=null] - Icon element to display before the text inside the span. Useful for adding visual indicators next to the text (optional).
 * @param {number} [props.enterDelay=0] - Milliseconds to wait before showing the tooltip.
 * @param {number} [props.leaveDelay=200] - Milliseconds the tooltip stays open after the pointer leaves, so it can be reached and scrolled.
 * @param {boolean} [props.disableInteractive=false] - When true the tooltip closes as soon as the pointer leaves the anchor.
 * @returns {JSX.Element} A JSX element with or without a tooltip based on conditions.
 */
const CustomTooltip = ({
    text,
    children = null,
    placement = PLACEMENT_MODEL.BOTTOM,
    arrow = false,
    tooltipSx = tooltipSxDefault,
    arrowSx = arrowSxDefault,
    classes = "",
    tooltipClasses="",
    showIfTruncated = false,
    icon = null,
    enterDelay = 0,
    leaveDelay = 200,
    disableInteractive = false
}) => {
    const ref = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);
    const [open, setOpen] = useState(false);

    // Compares the clamped size of the text with its natural size, 1px is tolerated for the rounding. The natural
    // height is the height of its lines (clientHeight with height auto), not the scrollHeight: the glyphs overflow a
    // small line-height and made a text of one line look truncated
    const measureTruncated = () => {
        const el = ref.current;
        if (!el) return false;
        const clampedHeight = el.clientHeight;
        const clampedWidth = el.clientWidth;

        const prevOverflow = el.style.overflow;
        const prevWebkitLineClamp = el.style.webkitLineClamp;
        const prevDisplay = el.style.display;
        const prevHeight = el.style.height;
        const prevMaxHeight = el.style.maxHeight;

        el.style.overflow = 'visible';
        el.style.webkitLineClamp = 'unset';
        el.style.display = 'block';
        el.style.height = 'auto';
        el.style.maxHeight = 'none';

        const naturalHeight = el.clientHeight;
        const naturalWidth = el.scrollWidth;

        el.style.overflow = prevOverflow;
        el.style.webkitLineClamp = prevWebkitLineClamp;
        el.style.display = prevDisplay;
        el.style.height = prevHeight;
        el.style.maxHeight = prevMaxHeight;

        return naturalWidth > clampedWidth + 1 || naturalHeight > clampedHeight + 1;
    };

    useEffect(() => {
        if (!children) setIsTruncated(measureTruncated());
    }, [text, children]);

    // The text is measured again when the pointer reaches it: it may have been measured out of view or before
    // its container was resized, so only the text truncated at that moment opens the tooltip
    const handleOpen = () => {
        const truncated = measureTruncated();
        setIsTruncated(truncated);
        setOpen(truncated);
    };

    const wrapWithTooltip = (node, onlyIfTruncated = false) => (
        <Tooltip
            title={text}
            placement={placement}
            arrow={arrow}
            enterDelay={enterDelay}
            leaveDelay={leaveDelay}
            disableInteractive={disableInteractive}
            componentsProps={{
                tooltip: { sx: tooltipSx },
                arrow: { sx: arrowSx },
            }}
            {...(onlyIfTruncated && { open, onOpen: handleOpen, onClose: () => setOpen(false) })}
            className={`cursor-pointer ${tooltipClasses}`}
        >
            {node}
        </Tooltip>
    );

    if (!showIfTruncated && children) return wrapWithTooltip(children);

    const span = (
        <span
            ref={ref}
            className={classes}
            onMouseEnter={showIfTruncated && !children && !isTruncated ? () => setIsTruncated(measureTruncated()) : undefined}
        >
            {icon ? icon : ''}{ text }
        </span>
    );
    return showIfTruncated && isTruncated ? wrapWithTooltip(<Box component="span">{span}</Box>, true) : span;
};

export default CustomTooltip;
